/**
 * The painted sky in dome space (STYLE.md §4). Shared by the sky material (uniform data) and the
 * TypeScript mirror of shaders/sky.frag used for calibration and tests.
 *
 * Dome coordinates are (azimuth, elevation). A free camera reads them straight from the view
 * direction, so the sky is fixed to the world. The painter camera maps its painted sky band onto
 * the dome with elevation = 90 deg x band fraction (STYLE §4.1) and keeps each pixel's true
 * azimuth, so the painter view and a walker at the painter's position see the same cloud field;
 * only the painted window layout differs, calibrated for the painter and frayed for free cameras
 * (skyFront.ts).
 */
import type { LinearRGB } from '../core/color';
import { hexToLinear } from '../core/color';
import { clamp, lerp, smoothstep, wrapAngle } from '../core/math';
import { DEFAULT_SEED } from '../core/config';
import type { Vec3 } from '../geo/types';
import type { PainterProjectionParams, SkyStop } from './types';
import { STYLE_CONSTANTS } from './painterProjection';
import { fbm3, MASS_OCTAVES, STREAK_FBM_STD, STREAK_OCTAVES } from './skyNoise';
import {
  FREE_FRONT,
  frontReachDeg,
  overheadWeight,
  windowAzimuth,
  type FrontField,
} from './skyFront';
import {
  WINDOW_COVERAGE,
  WINDOW_DARKNESS,
  WINDOW_THRESHOLDS,
  OUTSIDE_THRESHOLD,
  type BandValues,
} from './skyWindow';
import {
  OUTSIDE_LOOKUP,
  readWindow,
  type WindowLookup,
  type WindowTables,
} from './skyWindowLookup';

/** STYLE §4.1 gradient stops (dome elevation deg, colour); 0 deg is the haze colour (§3.2, §12.4). */
export const CLEAR_SKY_STOPS_HEX: readonly (readonly [number, string])[] = [
  [0.0, '#a7a97f'],
  [5.6, '#9fa77e'],
  [10.6, '#9ca57c'],
  [15.6, '#8fa07b'],
  [20.5, '#889c77'],
  [25.5, '#839772'],
  [30.5, '#79926e'],
  [35.4, '#6f8b6a'],
  [40.4, '#668563'],
  [45.3, '#5f8160'],
  [50.3, '#587d5e'],
  [55.3, '#527a5b'],
  [60.2, '#51785a'],
  [65.2, '#4e7659'],
  [70.2, '#4e7459'],
  [75.1, '#4c7153'],
  [80.1, '#4a6a4a'],
  [85.0, '#4b6747'],
];

/**
 * The overcast sheet (STYLE §4.1, last paragraph): median of the same 8-row bands over the
 * overcast columns 1195-1986, #254533 at the top to #1e4d3d just above the horizon. Index-aligned
 * with CLEAR_SKY_STOPS_HEX; below the lowest band the sheet keeps its horizon colour.
 */
export const OVERCAST_SHEET_HEX: readonly string[] = [
  '#1e4d3d',
  '#1e4d3d',
  '#1e4c3f',
  '#1d4b3d',
  '#1b4e42',
  '#1c4d41',
  '#1a4d42',
  '#1b4d41',
  '#1a4f44',
  '#1b4f42',
  '#1d4e41',
  '#1e4c40',
  '#1e4c40',
  '#1e4b3f',
  '#1f4a3d',
  '#1f493a',
  '#214635',
  '#254533',
];

/** STYLE §4.2: median cloud pixel and the darkest bank (palette class cloudDark). */
export const CLOUD_MEDIAN_HEX = '#1d4a3d';
export const CLOUD_DARK_HEX = '#183426';
export const CLOUD_COVERAGE = 0.555;
export const CLOUD_COVERAGE_OVERCAST = 0.972;

/** STYLE §4.2: autocorrelation 1/e lengths of the cloud deficit, 116.7 x 32.2 px in the 2000 px frame. */
export const CLOUD_ASPECT = 116.7 / 32.2;
/** Vertical 1/e length as a share of the painted sky height (row 0 to the painted horizon). */
export const CLOUD_HEIGHT_SHARE = 32.2 / STYLE_CONSTANTS.paintedHorizonRow;

/** 1/e autocorrelation length of the value-noise fbm in lattice cells (measured on the mirror). */
export const NOISE_CELL_1E = 0.664;
/**
 * Near the horizon the dome is a circle in noise space, so a step in azimuth is a chord that runs
 * along the lattice diagonals, where value noise decorrelates more slowly. Measured over 3000
 * seeds at 5-20 deg elevation, a 4% finer horizontal scale restores masses 3.6 x wider than tall.
 */
export const HORIZONTAL_CHORD_CORRECTION = 1.04;
/** Brush streaks: 1.4 deg tall, five times as wide, horizontal like the sky grain of STYLE §11. */
export const STREAK_HEIGHT_DEG = 1.4;
export const STREAK_ASPECT = 5;
export const STREAK_SEED_OFFSET = 7;
/* The painting's layout is looked up at a noisy azimuth so the fronts between its clear and
   overcast parts fray like painted cloud edges instead of following straight meridians. */
export const WARP_HEIGHT_DEG = 6;
export const WARP_ASPECT = 4;
export const WARP_AMPLITUDE_DEG = 0.8;
export const WARP_SEED_OFFSET = 13;

export const SKY_LOOK = {
  /** Half width of a cloud edge in noise units where coverage is 0.5 (soft painted edges). */
  edgeSoftness: 0.06,
  edgeSoftnessMin: 0.008,
  /** Half width where clouds break up into wisps (coverage at most 0.5). */
  edgeSoftnessOpen: 0.1,
  /** Streak amplitudes: cloud-edge displacement (noise units) and relative luminance. */
  streakEdge: 0.02,
  streakCloud: 0.1,
  streakClear: 0.035,
  /** Noise values between which clouds darken from the sheet to cloudDark. */
  darkFrom: 0.54,
  darkTo: 0.64,
} as const;

/**
 * Free cameras. The painter's band packs 90 deg of dome into about 100 rows at the 1376 px frame, so
 * the window's front and the edges of its overcast sheet, calibrated to stay closed there, stand as
 * hard-edged islands when a walker sees the dome at 16.7 px a degree. A free camera reads the
 * painted layout averaged over `knotBlur` knots and draws no cloud edge narrower than
 * `edgeSoftness` (half width in noise units). Both scale with one minus the painter amount, so the
 * painter view is unchanged. Chosen on round 11 captures of three free views against the scan's sky
 * band (STYLE §4.2): wider blurs stand the front up as a wall (skyFront.test.ts), softer edges wash
 * the masses into one veil.
 */
export const FREE_SKY = {
  knotBlur: 4,
  edgeSoftness: 0.25,
} as const;

export const PAINTER_WINDOW = {
  /** The painting's columns 0-2000 span +-16.0 deg of true azimuth at the sky band. */
  halfWidthDeg: 16,
  /** Blend from the painting's layout to the free cloud field outside the window. */
  marginDeg: 40,
  /* The painting's darkest bank lies at its right edge; carried through the whole margin it
     would stand in free views as a dark band from horizon to zenith. */
  darkMarginDeg: 12,
} as const;

export interface PainterSkyFrame {
  readonly forward: Vec3;
  readonly up: Vec3;
  readonly right: Vec3;
  readonly yawBearing: number;
  readonly pitch: number;
  readonly tanHorizon: number;
  readonly tanTop: number;
}

export interface SkyShading extends FrontField, WindowTables {
  readonly clearElevationsDeg: readonly number[];
  readonly clearColors: readonly LinearRGB[];
  readonly overcastColors: readonly LinearRGB[];
  readonly cloudDark: LinearRGB;
  readonly massScale: Vec3;
  readonly streakScale: Vec3;
  readonly warpScale: Vec3;
  readonly warpAmplitude: number;
  readonly seed: number;
  readonly outsideThreshold: number;
  readonly outsideSoftness: number;
  readonly freeKnotBlur: number;
  readonly freeEdgeSoftness: number;
  readonly frame: PainterSkyFrame;
}

const HALF_PI = Math.PI / 2;
const DEG = Math.PI / 180;

function dot(a: Vec3, b: Vec3): number {
  return a[0] * b[0] + a[1] * b[1] + a[2] * b[2];
}

export function clearSkyStops(): readonly SkyStop[] {
  return CLEAR_SKY_STOPS_HEX.map(([elevationDeg, hex]) => ({
    elevationDeg,
    color: hexToLinear(hex),
  }));
}

export type PainterFrameParams = Pick<
  PainterProjectionParams,
  | 'forwardDir'
  | 'rightDir'
  | 'horizonDepressionRad'
  | 'horizonElevationTop'
  | 'paintedHorizonFraction'
>;

/**
 * Recovers the painter camera's pitch and vertical tangents from the painter projection
 * parameters: the painted horizon row sits at (1 - 2 x rowFraction) of the half-height tangent.
 */
export function painterSkyFrame(pp: PainterFrameParams): PainterSkyFrame {
  const ndcHorizon = 1 - 2 * pp.paintedHorizonFraction;
  const span = pp.horizonElevationTop + pp.horizonDepressionRad;
  let lo = 1e-4;
  let hi = 2;
  for (let i = 0; i < 80; i++) {
    const mid = (lo + hi) / 2;
    if (Math.atan(mid) - Math.atan(ndcHorizon * mid) < span) lo = mid;
    else hi = mid;
  }
  const tanTop = (lo + hi) / 2;
  const pitch = pp.horizonElevationTop - Math.atan(tanTop);
  const f = pp.forwardDir;
  const c = Math.cos(pitch);
  const s = Math.sin(pitch);
  return {
    forward: [c * f[0], s, c * f[2]],
    up: [-s * f[0], c, -s * f[2]],
    right: pp.rightDir,
    yawBearing: Math.atan2(f[0], -f[2]),
    pitch,
    tanHorizon: ndcHorizon * tanTop,
    tanTop,
  };
}

export function bearingOf(dir: Vec3): number {
  const horizontal = Math.hypot(dir[0], dir[2]);
  return horizontal < 1e-9 ? 0 : Math.atan2(dir[0], -dir[2]);
}

/** Painted band fraction (0 at the painted horizon, 1 at the top of the frame) of a direction. */
export function painterBandFraction(dir: Vec3, frame: PainterSkyFrame): number {
  const along = dot(dir, frame.forward);
  if (along <= 1e-4) return -1;
  const tanY = dot(dir, frame.up) / along;
  return (tanY - frame.tanHorizon) / (frame.tanTop - frame.tanHorizon);
}

export interface DomeCoordinates {
  readonly azimuth: number;
  readonly elevation: number;
}

export function domeCoordinates(
  dir: Vec3,
  painterAmount: number,
  frame: PainterSkyFrame
): DomeCoordinates {
  const azimuth = bearingOf(dir);
  const free = Math.asin(clamp(dir[1], -1, 1));
  if (painterAmount <= 0) return { azimuth, elevation: free };
  const painted = HALF_PI * Math.min(painterBandFraction(dir, frame), 1);
  return { azimuth, elevation: lerp(free, painted, painterAmount) };
}

export function domeDirection(azimuth: number, elevation: number): Vec3 {
  const c = Math.cos(elevation);
  return [c * Math.sin(azimuth), Math.sin(elevation), -c * Math.cos(azimuth)];
}

/* Edges are soft wherever clouds break up; toward full cover they narrow so the overcast sheet
   stays closed instead of showing thin lines of clear sky. */
function edgeSoftness(coverage: number): number {
  if (coverage < 0.5) return SKY_LOOK.edgeSoftnessOpen;
  const spread = Math.sqrt(clamp(4 * coverage * (1 - coverage), 0, 1));
  return SKY_LOOK.edgeSoftness * spread + SKY_LOOK.edgeSoftnessMin;
}

function interpolateStops(
  elevationsDeg: readonly number[],
  colors: readonly LinearRGB[],
  elevationDeg: number
): LinearRGB {
  const last = elevationsDeg.length - 1;
  if (elevationDeg <= elevationsDeg[0]!) return colors[0]!;
  if (elevationDeg >= elevationsDeg[last]!) return colors[last]!;
  let i = 1;
  while (elevationsDeg[i]! < elevationDeg) i++;
  const t = (elevationDeg - elevationsDeg[i - 1]!) / (elevationsDeg[i]! - elevationsDeg[i - 1]!);
  const a = colors[i - 1]!;
  const b = colors[i]!;
  return [lerp(a[0], b[0], t), lerp(a[1], b[1], t), lerp(a[2], b[2], t)];
}

export function createSkyShading(stops: readonly SkyStop[], pp: PainterFrameParams): SkyShading {
  if (stops.length !== OVERCAST_SHEET_HEX.length) {
    throw new Error(
      `Sky stops (${String(stops.length)}) and overcast sheet colours (${String(OVERCAST_SHEET_HEX.length)}) must align`
    );
  }
  const massVertical = NOISE_CELL_1E / (CLOUD_HEIGHT_SHARE * HALF_PI);
  const massHorizontal = (massVertical / CLOUD_ASPECT) * HORIZONTAL_CHORD_CORRECTION;
  const streakVertical = NOISE_CELL_1E / (STREAK_HEIGHT_DEG * DEG);
  const streakHorizontal = streakVertical / STREAK_ASPECT;
  const warpVertical = NOISE_CELL_1E / (WARP_HEIGHT_DEG * DEG);
  const warpHorizontal = warpVertical / WARP_ASPECT;
  const frontVertical = NOISE_CELL_1E / (FREE_FRONT.heightDeg * DEG);
  const frontHorizontal = (frontVertical / CLOUD_ASPECT) * HORIZONTAL_CHORD_CORRECTION;
  return {
    clearElevationsDeg: stops.map((s) => s.elevationDeg),
    clearColors: stops.map((s) => s.color),
    overcastColors: OVERCAST_SHEET_HEX.map(hexToLinear),
    cloudDark: hexToLinear(CLOUD_DARK_HEX),
    massScale: [massHorizontal, massVertical, massHorizontal],
    streakScale: [streakHorizontal, streakVertical, streakHorizontal],
    warpScale: [warpHorizontal, warpVertical, warpHorizontal],
    warpAmplitude: WARP_AMPLITUDE_DEG * DEG,
    frontScale: [frontHorizontal, frontVertical, frontHorizontal],
    frontAmplitude: FREE_FRONT.amplitudeDeg * DEG,
    frontReach: frontReachDeg(PAINTER_WINDOW.halfWidthDeg + PAINTER_WINDOW.marginDeg) * DEG,
    overheadDeg: [FREE_FRONT.overheadFromDeg, FREE_FRONT.overheadToDeg],
    seed: DEFAULT_SEED,
    outsideThreshold: OUTSIDE_THRESHOLD,
    outsideSoftness: edgeSoftness(CLOUD_COVERAGE),
    freeKnotBlur: FREE_SKY.knotBlur,
    freeEdgeSoftness: FREE_SKY.edgeSoftness,
    windowThresholds: WINDOW_THRESHOLDS,
    windowSoftness: WINDOW_COVERAGE.map(
      (bands) => bands.map(edgeSoftness) as unknown as BandValues
    ),
    windowDarkness: WINDOW_DARKNESS,
    frame: painterSkyFrame(pp),
  };
}

export function massNoise(shading: SkyShading, d: Vec3): number {
  const s = shading.massScale;
  return fbm3(d[0] * s[0], d[1] * s[1], d[2] * s[2], shading.seed, MASS_OCTAVES);
}

export function streakNoise(shading: SkyShading, d: Vec3): number {
  const s = shading.streakScale;
  const n = fbm3(
    d[0] * s[0],
    d[1] * s[1],
    d[2] * s[2],
    shading.seed + STREAK_SEED_OFFSET,
    STREAK_OCTAVES
  );
  return (n - 0.5) / STREAK_FBM_STD;
}

export function warpNoise(shading: SkyShading, d: Vec3): number {
  const s = shading.warpScale;
  const n = fbm3(
    d[0] * s[0],
    d[1] * s[1],
    d[2] * s[2],
    shading.seed + WARP_SEED_OFFSET,
    STREAK_OCTAVES
  );
  return (n - 0.5) / STREAK_FBM_STD;
}

export function windowWeight(
  azimuthFromYaw: number,
  marginDeg: number = PAINTER_WINDOW.marginDeg
): number {
  const half = PAINTER_WINDOW.halfWidthDeg * DEG;
  return 1 - smoothstep(half, half + marginDeg * DEG, Math.abs(azimuthFromYaw));
}

export function clearGradient(shading: SkyShading, elevationDeg: number): LinearRGB {
  return interpolateStops(shading.clearElevationsDeg, shading.clearColors, elevationDeg);
}

export interface SkySample {
  readonly color: LinearRGB;
  readonly cloud: number;
  readonly darkness: number;
}

function windowLookup(
  shading: SkyShading,
  d: Vec3,
  azimuthFromYaw: number,
  elevationDeg: number,
  blurKnots: number
): WindowLookup {
  const lookupDeg = (azimuthFromYaw + shading.warpAmplitude * warpNoise(shading, d)) / DEG;
  return readWindow(shading, lookupDeg, elevationDeg, blurKnots);
}

/**
 * Mirror of sky.frag for one dome position (azimuth, elevation in radians); fray is 1 - painter
 * amount.
 */
export function skyAtDome(
  shading: SkyShading,
  azimuth: number,
  elevation: number,
  fray: number
): SkySample {
  const elevationDeg = elevation / DEG;
  const clear = clearGradient(shading, elevationDeg);
  if (elevation <= 0) return { color: clear, cloud: 0, darkness: 0 };
  const d = domeDirection(azimuth, elevation);
  const n = massNoise(shading, d);
  const streak = streakNoise(shading, d);
  const inWindow = windowAzimuth(shading, d, wrapAngle(azimuth - shading.frame.yawBearing), fray);
  const overhead = overheadWeight(shading, elevationDeg, fray);
  const w = windowWeight(inWindow) * overhead;
  const wd = windowWeight(inWindow, PAINTER_WINDOW.darkMarginDeg) * overhead;
  const win =
    w > 0
      ? windowLookup(shading, d, inWindow, elevationDeg, fray * shading.freeKnotBlur)
      : OUTSIDE_LOOKUP;
  const threshold = lerp(shading.outsideThreshold, win.threshold, w);
  const painted = lerp(shading.outsideSoftness, win.softness, w);
  const softness = lerp(painted, Math.max(painted, shading.freeEdgeSoftness), fray);
  const cloud = smoothstep(
    threshold - softness,
    threshold + softness,
    n + SKY_LOOK.streakEdge * streak
  );
  const darkness = lerp(smoothstep(SKY_LOOK.darkFrom, SKY_LOOK.darkTo, n), win.darkness, wd);
  const sheet = interpolateStops(shading.clearElevationsDeg, shading.overcastColors, elevationDeg);
  const clearGain = 1 + SKY_LOOK.streakClear * streak;
  const cloudGain = 1 + SKY_LOOK.streakCloud * streak;
  const color = [0, 1, 2].map((c) => {
    const body = lerp(sheet[c]!, shading.cloudDark[c]!, darkness) * cloudGain;
    return lerp(clear[c]! * clearGain, body, cloud);
  }) as unknown as LinearRGB;
  return { color, cloud, darkness };
}

/** Mirror of sky.frag for a view direction. */
export function skyRadiance(shading: SkyShading, dir: Vec3, painterAmount: number): SkySample {
  const { azimuth, elevation } = domeCoordinates(dir, painterAmount, shading.frame);
  return skyAtDome(shading, azimuth, Math.min(elevation, HALF_PI), 1 - painterAmount);
}
